import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	RangeControl,
	ToggleControl,
	ColorPicker,
	BaseControl,
	TextControl,
	FontSizePicker,
	SelectControl,
} from '@wordpress/components';



import './editor.scss';

export default function Edit({ attributes, setAttributes }) {
	const {
		progressName,
		percentage,
		fillColor,
		trackColor,
		showPercentage,
		progressHeight,
		textColor,
		percentageTextColor,
		textFontSize,
		percentageFontSize,
		textFontFamily,
		percentageFontFamily
	} = attributes;

	const blockProps = useBlockProps();

	const fontSizes = [
		{
			name: __('Small', 'wbd-progress-bar'),
			slug: 'small',
			size: 12,
		},
		{
			name: __('Normal', 'wbd-progress-bar'),
			slug: 'normal',
			size: 14,
		},
		{
			name: __('Medium', 'wbd-progress-bar'),
			slug: 'medium',
			size: 16,
		},
		{
			name: __('Large', 'wbd-progress-bar'),
			slug: 'large',
			size: 20,
		},
		{
			name: __('Extra Large', 'wbd-progress-bar'),
			slug: 'extra-large',
			size: 24,
		},
	];

	const percentageFontSizes = [
		{
			name: __('Extra Small', 'wbd-progress-bar'),
			slug: 'extra-small',
			size: 8,
		},
		{
			name: __('Small', 'wbd-progress-bar'),
			slug: 'small',
			size: 10,
		},
		{
			name: __('Normal', 'wbd-progress-bar'),
			slug: 'normal',
			size: 12,
		},
		{
			name: __('Medium', 'wbd-progress-bar'),
			slug: 'medium',
			size: 14,
		},
		{
			name: __('Large', 'wbd-progress-bar'),
			slug: 'large',
			size: 16,
		},
	];

	const fontFamilyOptions = [
		{ label: __('Inherit from theme', 'wbd-progress-bar'), value: 'inherit' },
		{ label: __('System Default', 'wbd-progress-bar'), value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
		{ label: __('Arial', 'wbd-progress-bar'), value: 'Arial, sans-serif' },
		{ label: __('Helvetica', 'wbd-progress-bar'), value: '"Helvetica Neue", Helvetica, sans-serif' },
		{ label: __('Georgia', 'wbd-progress-bar'), value: 'Georgia, serif' },
		{ label: __('Times New Roman', 'wbd-progress-bar'), value: '"Times New Roman", Times, serif' },
		{ label: __('Courier New', 'wbd-progress-bar'), value: '"Courier New", Courier, monospace' },
		{ label: __('Verdana', 'wbd-progress-bar'), value: 'Verdana, sans-serif' },
		{ label: __('Trebuchet MS', 'wbd-progress-bar'), value: '"Trebuchet MS", sans-serif' },
	];



	return (
		<>
			<InspectorControls>
				<PanelBody
					title={__('Progress Settings', 'wbd-progress-bar')}
					initialOpen={true}
				>
					


					<TextControl
						label={__('Progress Name', 'wbd-progress-bar')}
						value={progressName}
						onChange={(value) =>
							setAttributes({ progressName: value })
						}
						placeholder={__(
							'e.g., Project Completion',
							'wbd-progress-bar'
						)}
					/>

					<RangeControl
						label={__('Progress Percentage', 'wbd-progress-bar')}
						value={percentage}
						onChange={(value) =>
							setAttributes({ percentage: value })
						}
						min={0}
						max={100}

					/>

					<RangeControl
						label={__('Progress Height', 'wbd-progress-bar')}
						value={progressHeight}
						onChange={(value) =>
							setAttributes({ progressHeight: value })
						}
						min={6}
						max={40}
					/>

					<ToggleControl
						label={__('Show Percentage Text', 'wbd-progress-bar')}
						checked={showPercentage}
						onChange={(value) =>
							setAttributes({ showPercentage: value })
						}
					/>
				</PanelBody>

				<PanelBody
					title={__('Typography', 'wbd-progress-bar')}
					initialOpen={false}
				>
					<BaseControl
						label={__('Progress Name Font Family', 'wbd-progress-bar')}
					>
						<SelectControl
							value={textFontFamily}
							options={fontFamilyOptions}
							onChange={(value) =>
								setAttributes({ textFontFamily: value })
							}
						/>
					</BaseControl>

					<BaseControl
						label={__('Progress Name Font Size', 'wbd-progress-bar')}
					>
						<FontSizePicker
							fontSizes={fontSizes}
							value={textFontSize}
							onChange={(value) =>
								setAttributes({ textFontSize: value })
							}
							withReset
							fallbackFontSize={14}
						/>
					</BaseControl>

					<BaseControl
						label={__('Percentage Text Font Family', 'wbd-progress-bar')}
					>
						<SelectControl
							value={percentageFontFamily}
							options={fontFamilyOptions}
							onChange={(value) =>
								setAttributes({ percentageFontFamily: value })
							}
						/>
					</BaseControl>

					<BaseControl
						label={__(
							'Percentage Text Font Size',
							'wbd-progress-bar'
						)}
					>
						<FontSizePicker
							fontSizes={percentageFontSizes}
							value={percentageFontSize}
							onChange={(value) =>
								setAttributes({ percentageFontSize: value })
							}
							withReset
							fallbackFontSize={10}
						/>
					</BaseControl>
				</PanelBody>

				<PanelBody
					title={__('Color Settings', 'wbd-progress-bar')}
					initialOpen={false}
				>
					<BaseControl
						label={__('Fill Color', 'wbd-progress-bar')}
					>
						<ColorPicker
							color={fillColor}
							onChangeComplete={(value) =>
								setAttributes({ fillColor: value.hex })
							}
							disableAlpha
						/>
					</BaseControl>

					<BaseControl
						label={__('Track Color', 'wbd-progress-bar')}
					>
						<ColorPicker
							color={trackColor}
							onChangeComplete={(value) =>
								setAttributes({ trackColor: value.hex })
							}
							disableAlpha
						/>
					</BaseControl>

					<BaseControl
						label={__('Progress Name / Icon Color', 'wbd-progress-bar')}
					>
						<ColorPicker
							color={textColor}
							onChangeComplete={(value) =>
								setAttributes({ textColor: value.hex, iconColor: value.hex })
							}
							disableAlpha
						/>
					</BaseControl>

					<BaseControl
						label={__('Percentage Text Color', 'wbd-progress-bar')}
					>
						<ColorPicker
							color={percentageTextColor}
							onChangeComplete={(value) =>
								setAttributes({
									percentageTextColor: value.hex,
								})
							}
							disableAlpha
						/>
					</BaseControl>
				</PanelBody>
			</InspectorControls>

			<div {...blockProps}>
				{(progressName) && (
					<div
						className="progress-name"
						style={{
							color: textColor,
							fontSize: `${textFontSize}px`,
							fontFamily: textFontFamily,
						}}
					>
						
						{progressName}
					</div>
				)}
				<div
					className="progress-track"
					style={{
						backgroundColor: trackColor,
						height: `${progressHeight}px`,
					}}
				>
					<div
						className="progress-fill"
						style={{
							width: `${percentage}%`,
							backgroundColor: fillColor,
						}}
					>
						{showPercentage && (
							<span
								className="progress-percentage"
								style={{
									color: percentageTextColor,
									fontSize: `${percentageFontSize}px`,
									fontFamily: percentageFontFamily,
								}}
							>
								{percentage}%
							</span>
						)}
					</div>
				</div>
			</div>
		</>
	);
}
